import type { FileUploadData } from "../jben_form/file_upload";
import FileUpload from "../jben_form/file_upload";
import { type UploadedFile } from "@utils/uploads/uploaded_file.ts";
import { TextInput } from "@sprout/text_input";
import { MultiCheckbox } from "@sprout/checkbox";
import { type Option, MultiSelect, SingleSelect } from "@sprout/select";
import type UploadManager from "../../utils/uploads/upload_manager";
import { useBoardConfiguration } from "@hooks/use_board_configuration.ts";

export type FieldValue = string | string[] | UploadedFile | null;
export type OnSelectParam = Option | readonly Option[] | null;

export interface FieldProps {
  label: string;
  name: string;
  required: boolean;
  type:
    | "input_text"
    | "textarea"
    | "input_file"
    | "multi_value_single_select"
    | "multi_value_multi_select";
  options: Option[];
  allowManual?: boolean;
  error?: string;
  internal?: boolean;
  onChange: (value: FieldValue) => void;
  uploadManager?: UploadManager;
  multiSelectStyle?: string;
  value?: FieldValue;
  allowedFiletypes?: string[];
  onFocus?: () => void;
  selected?: OnSelectParam;
  disabled?: boolean;
}

export const Field = ({
  label,
  name,
  required,
  type,
  options,
  allowManual,
  error,
  onChange,
  uploadManager,
  multiSelectStyle,
  value,
  allowedFiletypes,
  onFocus,
  selected,
}: FieldProps) => {
  const { google_drive_enabled, enable_dropbox } = useBoardConfiguration();

  const handleChange = (newValue: string | Option | readonly Option[] | null) => {
    if (!newValue) {
      return onChange("");
    }

    if (typeof newValue === "string") {
      return onChange(newValue);
    }

    if (newValue instanceof Array) {
      return onChange(newValue.map((option) => option.value.toString()));
    }

    onChange((newValue as Option).value.toString());
  };

  const handleFileUploadChange = (data: FileUploadData) => {
    if (data.file) {
      onChange(data.file);
    } else if (data.text) {
      onChange(data.text);
    } else {
      onChange(null);
    }
  };

  const isCheckbox = multiSelectStyle === "checkbox";

  switch (type) {
    case "input_text":
      return (
        <div onFocus={onFocus}>
          <TextInput
            id={name}
            label={label}
            required={required}
            onChange={(event) => handleChange(event.currentTarget.value)}
            error={error}
            maxLength={255}
            value={(value as string) || ""}
          />
        </div>
      );
    case "textarea":
      return (
        <TextInput
          multiline
          rows={4}
          id={name}
          label={label}
          required={required}
          onChange={(event) => handleChange(event.currentTarget.value)}
          error={error}
        />
      );
    case "input_file": {
      const manualInputId = allowManual ? `${name}_text` : undefined;

      if (!uploadManager) {
        console.error("UploadManager not provided to Field");
        return null;
      }

      return (
        <FileUpload
          id={name}
          manualInputId={manualInputId}
          label={label}
          onChange={handleFileUploadChange}
          loadDropbox={enable_dropbox}
          allowManual={allowManual}
          errorMessage={error}
          uploadManager={uploadManager}
          required={required}
          loadGoogleDrive={google_drive_enabled}
          filetypes={allowedFiletypes}
          fileName={(value as UploadedFile)?.name}
        />
      );
    }
    case "multi_value_multi_select": {
      if (isCheckbox) {
        return (
          <MultiCheckbox
            id={name}
            options={options.map((o) => ({ label: o.label, value: o.value.toString() }))}
            description={label}
            required={required}
            error={error}
            isMulti
            onSelect={(selectedOptions) => handleChange(selectedOptions as Option | Option[])}
          />
        );
      }

      const selectedValues = (value as string[] | undefined) ?? [];
      const selectedOptions = options.filter((o) => selectedValues.includes(o.value.toString()));
      return (
        <MultiSelect
          label={label}
          required={required}
          options={options}
          values={selectedOptions}
          onChange={handleChange}
          error={error}
          clearable
        />
      );
    }
    case "multi_value_single_select": {
      const singleValue =
        selected !== undefined
          ? (selected as Option | null | undefined) ?? null
          : options.find((o) => o.value.toString() === (value as string)) ?? null;
      return (
        <SingleSelect
          label={label}
          required={required}
          options={options}
          value={singleValue}
          onChange={handleChange}
          error={error}
          clearable
        />
      );
    }
    default:
      return <></>;
  }
};
